// Price-related utility functions
import { parseLocale } from 'hooks';
import type { TPrice } from 'types/product';
import { getCookie } from './helpers';

// Helper to get price range string
export function getPriceRange(price: TPrice): string {
  if (price.priceDiscountFromFormatted && price.priceDiscountToFormatted) {
    return `${price.priceDiscountFromFormatted} - ${price.priceDiscountToFormatted}`;
  }
  if (price.priceFromFormatted && price.priceToFormatted) {
    return `${price.priceFromFormatted} - ${price.priceToFormatted}`;
  }
  return price.priceFromFormatted || price.priceToFormatted || '';
}

// Renamed for clarity
export function getPreviousPriceRange(price: TPrice): string {
  if (price.previousFormated) return price.previousFormated;
  if (price.priceFromFormatted && price.priceToFormatted) return `${price.priceFromFormatted} - ${price.priceToFormatted}`;
  return price.priceFromFormatted || price.priceToFormatted || '';
}

export function getDiscountString(price: TPrice): string {
  let discountString = price.discountStr;
  if (!discountString && typeof price.discount === 'number' && price.discount !== 0) {
    discountString = `-${price.discount}%`;
  }
  if (!discountString) return '';
  if (!discountString.startsWith('-')) {
    discountString = `-${discountString}`;
  }
  // If multiple dashes, only show the last part (e.g., '-20%-10%' => '-10%')
  const splitted = discountString.split('-').filter(Boolean);
  if (splitted.length > 1) {
    discountString = `-${splitted[splitted.length - 1]}`;
  }
  return discountString;
}

export type ObtainVoucherPricesV2Options = {
  data: {
    voucherPrices?: Record<string, TPrice>;
    sizes?: { value: string; voucherPrices?: Record<string, TPrice> }[];
    prices?: TPrice;
    [key: string]: any;
  };
  t: (parent: string, key: string) => string;
  locale: string;
  checkSize?: boolean;
  appliesCookie?: boolean;
  alternativeMessage?: string;
};

// V2 of obtainVoucherPrices, copied from utils/helpers.js
// NOTE: This is a direct copy, you may want to refactor or adapt it for future use.
export function obtainVoucherPricesV2({
  data,
  t,
  locale,
  checkSize = false,
  appliesCookie = false,
  alternativeMessage,
}: ObtainVoucherPricesV2Options): { message: string | null; info: TPrice & { appliedLabel?: string } } | null {
  if (typeof window === 'undefined') return null;
  const voucherData = data?.voucherPrices;
  if (!voucherData || Object.keys(voucherData).length === 0) return null;

  const { language, country } = parseLocale(locale);
  const searchParams = new URLSearchParams(window.location.search);
  const prevoucher = getCookie('PREVOUCHER') || false;
  let voucher = getCookie('BONDS') || false;
  let voucherID = null;
  const userHashInUrl = searchParams.get('usrhash');
  let userHash: string | null = getCookie('SELLIGENT_USRHASH') ?? userHashInUrl;
  const encodedURL = encodeURIComponent(window?.location?.href);
  const selectedSize = window?.selectedSizeForVoucher;

  let voucherInfo: TPrice | null = null;
  let voucherMessage = null;
  let displayMessage = false;
  let isPrevoucher = false;
  const anyNormalVoucher = Object.values(voucherData).some((item: any) => item.preLaunch === false);
  const firstVoucher = Object.keys(voucherData)[0] ?? '';
  const firstVoucherData = voucherData[firstVoucher];
  const isFirstVoucherPreVoucher = firstVoucherData?.preLaunch || false;

  if (firstVoucherData?.usrHash === false) userHash = null;

  if (userHash && !isFirstVoucherPreVoucher) {
    voucher = firstVoucher;
    if (appliesCookie) {
      // setCookieBonds(firstVoucher); // Not implemented here
    }
  }

  if (voucher) {
    if (Object.keys(voucherData).includes(voucher)) {
      voucherID = voucher;
      voucherInfo = voucherData[voucher] ?? null;
    } else {
      voucherInfo = voucherData[firstVoucher] ?? null;
      displayMessage = true;
    }
  } else if (prevoucher) {
    displayMessage = true;
    if (firstVoucher === prevoucher) {
      voucherID = prevoucher;
      isPrevoucher = true;
      voucherInfo = voucherData[prevoucher] ?? null;
    } else {
      isPrevoucher = !anyNormalVoucher;
    }
  } else {
    isPrevoucher = !anyNormalVoucher;
    displayMessage = isPrevoucher ? firstVoucherData?.usrHash === true && userHash !== null : firstVoucherData?.adqCampaign !== false;
    voucherInfo = anyNormalVoucher && firstVoucherData?.usrHash === true && userHash !== null ? (voucherData[firstVoucher] ?? null) : null;
  }

  if (displayMessage) {
    if (voucherInfo === null) voucherInfo = firstVoucherData ?? null;
    const { currentFormated: vPrice, discount: vDiscount, pricePerSize, voucherType } = voucherInfo ?? {};

    let pricePerSizeLabel = pricePerSize ? `${voucherInfo?.priceDiscountFromFormatted} - ${voucherInfo?.priceDiscountToFormatted}` : '';
    if (pricePerSize && voucherInfo?.voucherRangePrices) {
      pricePerSizeLabel = `${voucherInfo?.voucherRangePrices?.priceFromFormatted} - ${voucherInfo?.voucherRangePrices?.priceToFormatted}`;
    }

    if (checkSize && selectedSize) {
      const voucherPrices = data?.sizes?.find((item: any) => item.value === selectedSize)?.voucherPrices;
      if (!voucherPrices) {
        console.error('No voucherPrices per size for product with voucherPrices.');
        return null;
      }
      if (voucherID === null) {
        [voucherID] = Object.keys(voucherPrices);
      }
      pricePerSizeLabel = voucherPrices?.[voucherID ?? '']?.currentFormated ?? '';
    }

    if (isPrevoucher && voucherInfo) {
      const messageKey = 'prevoucher.price.message.subscribed';
      voucherMessage = t('listado.productos', messageKey)
        .replace('<TAG price>', pricePerSize ? pricePerSizeLabel : (vPrice ?? ''))
        .replace('<TAG discount>', String(vDiscount ?? ''))
        .replace('<TAG URL ENCODED>', encodedURL)
        .replace('<TAG COUNTRY_CODE UPPER>', country)
        .replace('<TAG LANGUAGE_CODE LOWER>', language)
        .replace('<TAG USR_HASH>', userHash ?? '');
      return { message: voucherMessage, info: voucherInfo };
    } else {
      const baseMessageKey = `voucher.${alternativeMessage ? alternativeMessage : `price.message${`.${voucherType}`}`}`;
      if (!userHash) {
        voucherMessage = t('listado.productos', baseMessageKey)
          .replace('<TAG price>', pricePerSize ? pricePerSizeLabel : (vPrice ?? ''))
          .replace('<TAG discount>', String(vDiscount ?? ''))
          .replace('<TAG URL ENCODED>', encodedURL)
          .replace('<TAG COUNTRY_CODE UPPER>', country)
          .replace('<TAG LANGUAGE_CODE LOWER>', language);
        return { message: voucherMessage, info: voucherInfo ?? {} };
      }
    }
  }

  if (displayMessage === false && !isPrevoucher && voucherInfo) {
    if (appliesCookie) {
      // setCookie('AUTO_BONDS', '1', 24, 'DOMAIN'); // Not implemented here
    }
    const { voucherType } = voucherInfo;
    const voucherTypeToUseInLabel = data?.prices?.discount !== undefined ? voucherType : 'original.price';
    voucherInfo.appliedLabel = t('listado.productos', `voucher.price.message.${voucherTypeToUseInLabel}.applied`);

    if (voucherTypeToUseInLabel === 'original.price') {
      voucherInfo.discountStr =
        voucherInfo?.discountStr && voucherInfo?.discountStr?.split('-')?.length < 2 ? `-${voucherInfo?.discountStr}` : (voucherInfo?.discountStr ?? '');
    }

    if (voucherType === 'final.price' && data?.prices?.discount !== undefined) {
      voucherInfo.discountStr = `-${data?.prices?.discount}%`;
      voucherInfo.appliedLabel = voucherInfo?.appliedLabel && voucherInfo?.appliedLabel?.replace('<TAG discount>', String(voucherInfo?.discount ?? ''));
    }
  }

  if (!voucherInfo) return null;

  if (voucherInfo?.appliedLabel?.includes('<TAG discount>') && voucherInfo?.discount && voucherInfo?.discount > 0) {
    voucherInfo.appliedLabel = voucherInfo.appliedLabel.replace('<TAG discount>', String(voucherInfo?.discount ?? ''));
  }

  if (!isPrevoucher && voucherInfo?.adqCampaign === false) {
    if (displayMessage) {
      return null;
    } else {
      return { message: '', info: voucherInfo };
    }
  }

  return { message: voucherMessage, info: voucherInfo };
}

export function getCurrencySymbol({ locale, currency }: { locale: string; currency: string }) {
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(0).replace(/[0-9\s\.,]/g, '');
  } catch (error) {
    console.error('Error getting currency symbol', error);
    console.error(`props: ${JSON.stringify({ locale, currency })}`);
    return new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(0).replace(/[0-9\s\.,]/g, '');
  }
}
